/**
 * Global kill switch for the "what's new" feature-announcement popup system
 * (auto-shown modal + the "New Features" menu entry's notification dot).
 * Flip back to true to re-enable — the modal, hooks and slide data below are
 * otherwise left fully intact.
 */
export const FEATURE_ANNOUNCEMENTS_ENABLED = false;

export interface IconValue {
  icon: string;
  set: "lucide" | "mdi" | "tactic";
}

export interface FeatureSlideTheme {
  accent: string;
  iconGlow: string;
  iconBg: string;
  badgeText: string;
  badgeBg: string;
  badgeBorder: string;
  ctaGradient: string;
  ctaHover: string;
  ctaShadow: string;
  ctaShadowHover: string;
  featureIconBg: string;
  featureIconText: string;
}

export interface FeatureSlideFeatureData {
  icon: IconValue;
  title: string;
  description: string;
}

export type FeatureTryActionType =
  | "redirect"
  | "external-url"
  | "open-tessa"
  | "open-global-search";

export interface FeatureTryAction {
  type: FeatureTryActionType;
  /** URL for redirect (internal) or external-url actions */
  url?: string;
}

/** Confetti intensity level: 1 = minimal, 5 = maximum celebration */
export type ConfettiIntensity = 1 | 2 | 3 | 4 | 5;

/** Feature flags that can gate feature announcements */
export type FeatureAnnouncementFlag = "tessa" | "workplace_requests";

export interface FeatureSlideData {
  id: string;
  icon: IconValue;
  badge: string;
  title: string;
  description: string;
  themeName: "purple" | "emerald" | "cyan";
  features: FeatureSlideFeatureData[];
  shortcut?: {
    keys: string[];
    description: string;
  };
  ctaText: string;
  releaseDate: string;
  tryAction: FeatureTryAction;
  /** Confetti intensity level: 1 = minimal, 5 = maximum celebration. Default: 3 */
  confettiIntensity?: ConfettiIntensity;
  /** Feature flag required to show this announcement. If not set, always shown. */
  requiredFeatureFlag?: FeatureAnnouncementFlag;
  /** If true, only admin users will see this announcement. Default: false */
  adminOnly?: boolean;
}

export const FEATURE_SLIDE_THEMES: Record<string, FeatureSlideTheme> = {
  purple: {
    accent: "bg-gradient-to-r from-[#4F48F7] via-[#656CFF] to-[#C43DA0]",
    iconGlow: "bg-gradient-to-r from-[#4F48F7] to-[#656CFF]",
    iconBg: "bg-gradient-to-br from-[#4F48F7] to-[#656CFF]",
    badgeText: "text-[#4F48F7]",
    badgeBg: "bg-[#4F48F7]/10",
    badgeBorder: "border-[#4F48F7]/20",
    ctaGradient: "from-[#0F2449] to-[#1C4372]",
    ctaHover: "hover:from-[#1C4372] hover:to-[#4072A5]",
    ctaShadow: "shadow-[#0F2449]/25",
    ctaShadowHover: "hover:shadow-[#0F2449]/40",
    featureIconBg: "from-[#4F48F7]/10 to-[#656CFF]/10",
    featureIconText: "text-[#4F48F7]"
  },
  emerald: {
    accent: "bg-gradient-to-r from-[#00D579] via-[#48A1F7] to-[#89C9FF]",
    iconGlow: "bg-gradient-to-r from-[#00D579] to-[#48A1F7]",
    iconBg: "bg-gradient-to-br from-[#00D579] to-[#48A1F7]",
    badgeText: "text-[#00D579]",
    badgeBg: "bg-[#00D579]/10",
    badgeBorder: "border-[#00D579]/20",
    ctaGradient: "from-[#0F2449] to-[#1C4372]",
    ctaHover: "hover:from-[#1C4372] hover:to-[#4072A5]",
    ctaShadow: "shadow-[#0F2449]/25",
    ctaShadowHover: "hover:shadow-[#0F2449]/40",
    featureIconBg: "from-[#00D579]/10 to-[#48A1F7]/10",
    featureIconText: "text-[#00D579]"
  },
  cyan: {
    accent: "bg-gradient-to-r from-[#06B6D4] via-[#0EA5E9] to-[#3B82F6]",
    iconGlow: "bg-gradient-to-r from-[#06B6D4] to-[#0EA5E9]",
    iconBg: "bg-gradient-to-br from-[#06B6D4] to-[#0EA5E9]",
    badgeText: "text-[#06B6D4]",
    badgeBg: "bg-[#06B6D4]/10",
    badgeBorder: "border-[#06B6D4]/20",
    ctaGradient: "from-[#0F2449] to-[#1C4372]",
    ctaHover: "hover:from-[#1C4372] hover:to-[#4072A5]",
    ctaShadow: "shadow-[#0F2449]/25",
    ctaShadowHover: "hover:shadow-[#0F2449]/40",
    featureIconBg: "from-[#06B6D4]/10 to-[#0EA5E9]/10",
    featureIconText: "text-[#06B6D4]"
  }
};

export const FEATURE_SLIDES: FeatureSlideData[] = [
  {
    id: "tessa",
    icon: { icon: "Sparkles", set: "lucide" },
    badge: "New Feature",
    title: "Meet Tessa",
    description:
      "Your AI-powered workplace assistant. Ask questions in natural language and get instant answers about your workplace.",
    themeName: "purple",
    features: [
      {
        icon: { icon: "MessageCircle", set: "lucide" },
        title: "Natural Conversations",
        description: "Ask anything about your workplace in plain English"
      },
      {
        icon: { icon: "MapPin", set: "lucide" },
        title: "Find Colleagues",
        description: "See where your teammates are working today"
      },
      {
        icon: { icon: "Calendar", set: "lucide" },
        title: "Manage Schedules",
        description: "View and manage your workplace reservations"
      },
      {
        icon: { icon: "Users", set: "lucide" },
        title: "Team Insights",
        description: "Get instant visibility into team availability"
      }
    ],
    shortcut: {
      keys: ["Cmd", "Shift", "A"],
      description: "to open Tessa anytime"
    },
    ctaText: "Try Tessa Now",
    releaseDate: "2026-01-29",
    tryAction: { type: "open-tessa" },
    confettiIntensity: 5,
    requiredFeatureFlag: "tessa"
  },
  {
    id: "global-search",
    icon: { icon: "Search", set: "lucide" },
    badge: "New Feature",
    title: "Global Search",
    description:
      "Find anything instantly. Search across people, places, reservations, and more with our powerful new search.",
    themeName: "emerald",
    features: [
      {
        icon: { icon: "Zap", set: "lucide" },
        title: "Lightning Fast",
        description: "Get results as you type with instant search"
      },
      {
        icon: { icon: "Users", set: "lucide" },
        title: "Find People",
        description: "Search for colleagues by name, team, or location"
      },
      {
        icon: { icon: "MapPin", set: "lucide" },
        title: "Discover Spaces",
        description: "Find available desks, rooms, and amenities"
      },
      {
        icon: { icon: "FileText", set: "lucide" },
        title: "Search Everything",
        description: "Reservations, requests, and resources in one place"
      }
    ],
    shortcut: {
      keys: ["Cmd", "K"],
      description: "to search anywhere"
    },
    ctaText: "Try Search Now",
    releaseDate: "2026-01-20",
    tryAction: { type: "open-global-search" },
    confettiIntensity: 3
  },
  {
    id: "wifi-checkin",
    icon: { icon: "Wifi", set: "lucide" },
    badge: "New Feature",
    title: "WiFi Check-in",
    description:
      "Automatically check in when you connect to your office WiFi network. No more manual check-ins required.",
    themeName: "cyan",
    features: [
      {
        icon: { icon: "Zap", set: "lucide" },
        title: "Automatic Check-in",
        description: "Check in seamlessly when you connect to office WiFi"
      },
      {
        icon: { icon: "Clock", set: "lucide" },
        title: "Save Time",
        description: "No more forgetting to check in manually"
      },
      {
        icon: { icon: "Shield", set: "lucide" },
        title: "Secure & Private",
        description: "Your location is only shared when on trusted networks"
      },
      {
        icon: { icon: "Settings", set: "lucide" },
        title: "Easy Setup",
        description: "Configure your trusted networks in settings"
      }
    ],
    ctaText: "Set Up WiFi Check-in",
    releaseDate: "2026-01-30",
    tryAction: { type: "redirect", url: "/settings/organization/wifi" },
    confettiIntensity: 2,
    adminOnly: true
  }
];
